import React, { useMemo, useRef, useEffect, useState } from "react";
import { Plus, Car, Truck, RefreshCw, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import type { ShiftRow, DragData } from "./types";
import { getShiftColour, driverDisplayName, calcWorkedHours } from "./utils";
import { loadVehicleTypes, getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";

/* ─── Constants ─── */
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_WIDTH = 80;
const TIMELINE_WIDTH = HOURS.length * HOUR_WIDTH;
const DRIVER_COL_WIDTH = 210;
const ROW_HEIGHT = 56;
const ROW_GAP = 4;
const HEADER_HEIGHT = 36;
const HEATMAP_HEIGHT = 48;
const SLOTS = 48; // 30-min intervals

const formatHour = (h: number): string => {
  if (h === 0) return "12AM";
  if (h < 12) return `${h}AM`;
  if (h === 12) return "12PM";
  return `${h - 12}PM`;
};

const timeToHours = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return h + m / 60;
};

const formatDuration = (decimalHours: number): string => {
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const formatTime12 = (hhmm: string): string => {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const stringToColour = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colours = [
    "#3B82F6",
    "#EF4444",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EC4899",
    "#06B6D4",
    "#F97316",
    "#6366F1",
    "#14B8A6",
    "#E11D48",
    "#84CC16",
    "#0EA5E9",
    "#D946EF",
    "#22D3EE",
  ];
  return colours[Math.abs(hash) % colours.length];
};

const getInitials = (shift: ShiftRow): string => {
  if (shift.driver_firstname && shift.driver_lastname) {
    return (shift.driver_firstname[0] + shift.driver_lastname[0]).toUpperCase();
  }
  const parts = shift.driver_name.split(/[\s_-]+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return shift.driver_name.substring(0, 2).toUpperCase();
};

/* ─── Types ─── */
interface DriverGroup {
  driverName: string;
  displayName: string;
  initials: string;
  avatarColour: string;
  totalHours: number;
  shifts: ShiftRow[];
}

interface CoverageTarget {
  start_time: string;
  end_time: string;
  min_drivers: number;
  day_of_week: number | null;
}

interface TimelineDayViewProps {
  shifts: ShiftRow[];
  dateStr: string;
  onEditShift: (shift: ShiftRow) => void;
  onCreateShift: () => void;
}

/* ─── Shift Bar Component ─── */
const ShiftBar: React.FC<{
  shift: ShiftRow;
  onEdit: (shift: ShiftRow) => void;
  vehicleTypes: VehicleTypeRow[];
}> = ({ shift, onEdit, vehicleTypes }) => {
  const colour = getShiftColour(shift);
  const startH = timeToHours(shift.start_hhmm);
  const endH = timeToHours(shift.end_hhmm);
  const isCancelled = shift.status === "cancelled";

  let durationH = endH - startH;
  if (durationH <= 0) durationH += 24;

  const leftPx = startH * HOUR_WIDTH;
  const widthPx = Math.max(durationH * HOUR_WIDTH, 60);
  const workedH = calcWorkedHours(shift.start_hhmm, shift.end_hhmm, shift.break_minutes);
  const isSTS = shift.shift_type_code?.toUpperCase().includes("STS");
  const isSpecial = ["LEAVE", "SICK", "TRAINING", "STANDBY"].includes(
    shift.shift_type_code?.toUpperCase() || ""
  );

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    const dragData: DragData = {
      shiftId: shift.id,
      sourceDate: shift.shift_date,
    };

    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("opacity-40");
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove("opacity-40");
  };

  const secondLine: string[] = [];
  if (shift.vehicle_type) secondLine.push(shift.vehicle_type);
  if (shift.shift_type_label) secondLine.push(shift.shift_type_label);
  if (shift.vehicle_number) secondLine.push(shift.vehicle_number);

  return (
    <div
      draggable={!isCancelled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={(e) => {
        e.stopPropagation();
        onEdit(shift);
      }}
      className={`absolute top-1 cursor-pointer group transition-all hover:brightness-110 hover:shadow-lg hover:z-20 select-none ${
        isCancelled ? "opacity-30" : ""
      }`}
      style={{
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        height: `${ROW_HEIGHT - 8}px`,
        borderRadius: "6px",
        backgroundColor: isSTS
          ? "transparent"
          : isSpecial
          ? `${colour}25`
          : `${colour}CC`,
        border: isSTS
          ? `2px dashed ${colour}`
          : isSpecial
          ? `1px solid ${colour}50`
          : "none",
        zIndex: 10,
      }}
    >
      <div className="px-2.5 py-1 h-full flex flex-col justify-center overflow-hidden">
        <div className="flex items-center gap-1.5">
          <span
            className="text-xs font-bold truncate"
            style={{ color: isSTS || isSpecial ? colour : "#fff" }}
          >
            {formatTime12(shift.start_hhmm)} - {formatTime12(shift.end_hhmm)}
          </span>
          <span
            className="text-xs font-semibold whitespace-nowrap"
            style={{ color: isSTS || isSpecial ? `${colour}BB` : "rgba(255,255,255,0.75)" }}
          >
            • {formatDuration(workedH)}
          </span>
          {shift.repeat_group_id && (
            <RefreshCw
              className="h-3 w-3 shrink-0 ml-auto"
              style={{ color: isSTS || isSpecial ? `${colour}99` : "rgba(255,255,255,0.6)" }}
            />
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          {shift.vehicle_number && (
            <>
              {getIconStyle(shift.vehicle_type, vehicleTypes) === "truck" ? (
                <Truck
                  className="h-3 w-3 shrink-0"
                  style={{ color: isSTS || isSpecial ? `${colour}88` : "rgba(255,255,255,0.6)" }}
                />
              ) : (
                <Car
                  className="h-3 w-3 shrink-0"
                  style={{ color: isSTS || isSpecial ? `${colour}88` : "rgba(255,255,255,0.6)" }}
                />
              )}
            </>
          )}
          <span
            className="text-[11px] truncate"
            style={{ color: isSTS || isSpecial ? `${colour}AA` : "rgba(255,255,255,0.7)" }}
          >
            {secondLine.join(" • ")}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ─── Coverage Heatmap ─── */
const CoverageHeatmap: React.FC<{
  shifts: ShiftRow[];
  dateStr: string;
  targets: CoverageTarget[];
}> = ({ shifts, dateStr, targets }) => {
  const slotData = useMemo(() => {
    const counts = new Array(SLOTS).fill(0);
    const activeShifts = shifts.filter((s) => s.status !== "cancelled");

    for (const s of activeShifts) {
      const startH = timeToHours(s.start_hhmm);
      let endH = timeToHours(s.end_hhmm);
      if (endH <= startH) endH += 24;

      for (let slot = 0; slot < SLOTS; slot++) {
        const slotStart = slot * 0.5;
        const slotEnd = slotStart + 0.5;
        if (startH < slotEnd && endH > slotStart) {
          counts[slot]++;
        }
        if (endH > 24 && slotStart < endH - 24) {
          counts[slot]++;
        }
      }
    }

    const dayOfWeek = new Date(dateStr + "T00:00:00").getDay();

    const targetMin = new Array(SLOTS).fill(0);
    for (const t of targets) {
      const tStart = timeToHours(t.start_time);
      let tEnd = timeToHours(t.end_time);
      if (tEnd <= tStart) tEnd += 24;

      if (t.day_of_week !== null && t.day_of_week !== dayOfWeek) continue;

      for (let slot = 0; slot < SLOTS; slot++) {
        const slotStart = slot * 0.5;
        const slotEnd = slotStart + 0.5;
        if (tStart < slotEnd && tEnd > slotStart) {
          targetMin[slot] = Math.max(targetMin[slot], t.min_drivers);
        }
      }
    }

    const maxCount = Math.max(...counts, 1);

    return counts.map((count, i) => ({
      count,
      target: targetMin[i],
      belowTarget: targetMin[i] > 0 && count < targetMin[i],
      pct: (count / Math.max(maxCount, 5)) * 100,
      colour:
        count === 0
          ? "bg-red-500/80"
          : count < 3
          ? "bg-yellow-500/70"
          : "bg-green-500/70",
    }));
  }, [shifts, dateStr, targets]);

  const slotWidth = TIMELINE_WIDTH / SLOTS;

  return (
    <div className="relative" style={{ height: HEATMAP_HEIGHT, width: TIMELINE_WIDTH }}>
      <div className="absolute inset-0 flex items-end">
        {slotData.map((slot, i) => (
          <div
            key={i}
            className="flex flex-col items-center justify-end"
            style={{ width: slotWidth, height: "100%" }}
            title={`${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 === 0 ? "00" : "30"} — ${slot.count} driver${slot.count !== 1 ? "s" : ""}${slot.target > 0 ? ` (target: ${slot.target})` : ""}`}
          >
            <div
              className={`w-[85%] rounded-t-sm transition-all ${slot.colour} ${
                slot.belowTarget ? "ring-1 ring-red-400 animate-pulse" : ""
              }`}
              style={{
                height: `${Math.max(slot.pct, slot.count > 0 ? 12 : 4)}%`,
                minHeight: slot.count > 0 ? "4px" : "2px",
              }}
            />
          </div>
        ))}
      </div>
      <div className="absolute bottom-0 left-0 right-0 border-t border-gray-700/50" />
    </div>
  );
};

/* ─── Main Component ─── */
const TimelineDayView: React.FC<TimelineDayViewProps> = ({
  shifts,
  dateStr,
  onEditShift,
  onCreateShift,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [nowLineX, setNowLineX] = useState<number | null>(null);
  const [showCoverage, setShowCoverage] = useState(false);
  const [coverageTargets, setCoverageTargets] = useState<CoverageTarget[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("roster_coverage_targets")
        .select("start_time, end_time, min_drivers, day_of_week")
        .eq("is_active", true);
      if (data) setCoverageTargets(data);
    };
    load();
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  const driverGroups: DriverGroup[] = useMemo(() => {
    const map = new Map<string, ShiftRow[]>();
    for (const s of shifts) {
      const existing = map.get(s.driver_name) || [];
      existing.push(s);
      map.set(s.driver_name, existing);
    }

    const groups: DriverGroup[] = [];
    for (const [driverName, driverShifts] of map.entries()) {
      const first = driverShifts[0];
      const totalHours = driverShifts
        .filter((s) => s.status !== "cancelled")
        .reduce((sum, s) => sum + (s.worked_hours || 0), 0);

      groups.push({
        driverName,
        displayName: driverDisplayName(first),
        initials: getInitials(first),
        avatarColour: stringToColour(driverName),
        totalHours,
        shifts: driverShifts.sort((a, b) => a.start_hhmm.localeCompare(b.start_hhmm)),
      });
    }

    groups.sort((a, b) => {
      const aStart = a.shifts[0]?.start_hhmm || "99:99";
      const bStart = b.shifts[0]?.start_hhmm || "99:99";
      return aStart.localeCompare(bStart);
    });

    return groups;
  }, [shifts]);

  const driverRowData = useMemo(() => {
    let currentY = 0;
    return driverGroups.map((group) => {
      const rowCount = group.shifts.length || 1;
      const groupHeight = rowCount * (ROW_HEIGHT + ROW_GAP);
      const startY = currentY;
      currentY += groupHeight;
      return { ...group, startY, groupHeight, rowCount };
    });
  }, [driverGroups]);

  const totalHeight = driverRowData.reduce((sum, d) => sum + d.groupHeight, 0);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 5 * HOUR_WIDTH;
    }
  }, [dateStr]);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      if (todayStr === dateStr) {
        const hours = now.getHours() + now.getMinutes() / 60;
        setNowLineX(hours * HOUR_WIDTH);
      } else {
        setNowLineX(null);
      }
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [dateStr]);

  if (shifts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <p className="text-lg mb-4">No shifts for this day</p>
        <Button size="sm" onClick={onCreateShift}>
          <Plus className="h-4 w-4 mr-1" /> Add Shift
        </Button>
      </div>
    );
  }

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-900/60">
      <div className="flex">
        <div
          className="shrink-0 border-r border-gray-700 bg-gray-900/80 z-20"
          style={{ width: DRIVER_COL_WIDTH }}
        >
          <div
            className="border-b border-gray-700 flex items-center justify-between px-2"
            style={{ height: HEADER_HEIGHT }}
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-gray-400 hover:text-blue-400"
              onClick={onCreateShift}
              title="Add shift"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-2 text-xs gap-1 ${
                showCoverage ? "text-blue-400 bg-blue-500/10" : "text-gray-500 hover:text-gray-300"
              }`}
              onClick={() => setShowCoverage((p) => !p)}
              title="Toggle coverage heatmap"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Coverage</span>
            </Button>
          </div>

          <div style={{ height: totalHeight }}>
            {driverRowData.map((group) => (
              <div
                key={group.driverName}
                className="flex items-center gap-2.5 px-3 border-b border-gray-800/60"
                style={{ height: group.groupHeight }}
              >
                <div
                  className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: group.avatarColour }}
                >
                  {group.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate leading-tight">
                    {group.displayName}
                  </p>
                  <p className="text-xs text-gray-400 leading-tight">
                    {formatDuration(group.totalHours)} • $0.00
                  </p>
                </div>
              </div>
            ))}
          </div>

          {showCoverage && (
            <div
              className="flex items-center px-3 border-t border-gray-700 bg-gray-900/60"
              style={{ height: HEATMAP_HEIGHT }}
            >
              <div className="text-[10px] text-gray-400 leading-tight">
                <div className="font-medium text-gray-300 mb-0.5">Staffing</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-sm bg-green-500/70" /> 3+
                  <span className="w-2 h-2 rounded-sm bg-yellow-500/70 ml-1" /> 1-2
                  <span className="w-2 h-2 rounded-sm bg-red-500/80 ml-1" /> 0
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-x-auto" ref={scrollRef}>
          <div style={{ width: TIMELINE_WIDTH, minWidth: "100%" }}>
            <div
              className="flex border-b border-gray-700 sticky top-0 bg-gray-900/95 z-10"
              style={{ height: HEADER_HEIGHT }}
            >
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="shrink-0 flex items-center justify-center text-xs text-gray-400 font-medium border-r border-gray-800/40"
                  style={{ width: HOUR_WIDTH }}
                >
                  {formatHour(h)}
                </div>
              ))}
            </div>

            <div className="relative" style={{ height: totalHeight }}>
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="absolute top-0 bottom-0 border-r border-gray-800/30"
                  style={{ left: h * HOUR_WIDTH }}
                />
              ))}
              {HOURS.map((h) => (
                <div
                  key={`half-${h}`}
                  className="absolute top-0 bottom-0 border-r border-gray-800/15"
                  style={{ left: h * HOUR_WIDTH + HOUR_WIDTH / 2 }}
                />
              ))}

              {nowLineX !== null && (
                <div
                  className="absolute top-0 bottom-0 z-30 pointer-events-none"
                  style={{ left: nowLineX }}
                >
                  <div className="w-0.5 h-full bg-red-500/70" />
                  <div
                    className="absolute -top-1 -left-1.5 w-3 h-3 rounded-full bg-red-500"
                    style={{ top: -2 }}
                  />
                </div>
              )}

              {driverRowData.map((group) => (
                <div key={group.driverName}>
                  <div
                    className="absolute left-0 right-0 border-b border-gray-800/40"
                    style={{
                      top: group.startY,
                      height: group.groupHeight,
                    }}
                  />
                  {group.shifts.map((shift, idx) => {
                    const rowY = group.startY + idx * (ROW_HEIGHT + ROW_GAP);
                    return (
                      <div
                        key={shift.id}
                        className="absolute"
                        style={{
                          top: rowY,
                          left: 0,
                          width: TIMELINE_WIDTH,
                          height: ROW_HEIGHT,
                        }}
                      >
                        <ShiftBar shift={shift} onEdit={onEditShift} vehicleTypes={vehicleTypes} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {showCoverage && (
              <div className="border-t border-gray-700">
                <CoverageHeatmap
                  shifts={shifts}
                  dateStr={dateStr}
                  targets={coverageTargets}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineDayView;